/**
 * Client-side mirror of the shared Texas Hold'em table. `World3D` rebuilds a plain
 * snapshot from the synced schema (plus the owner-only seat assignment + the private
 * hole cards delivered by the PokerHole message) and publishes it here; the poker
 * overlay subscribes. The server is authoritative — this is a read model for rendering.
 */
import type { CharacterAppearance } from "@ghostland/shared";

export interface PokerSeatView {
  index: number;
  occupied: boolean;
  name: string;
  appearance: CharacterAppearance;
  connected: boolean;
  chips: number;
  /** Chips committed on the current street. */
  bet: number;
  /** 0 or 2 — how many hole cards to render face-down for this seat. */
  cardCount: number;
  /** Revealed hole cards at showdown (space-separated); "" while concealed. */
  revealed: string;
  /** empty | out | active | folded | allin | won (transient banner) */
  status: string;
  /** Last action label for table feedback ("call" | "raise" | "fold" | "check" | "allin" | "post" | "won"). */
  lastAction: string;
}

export interface PokerTableView {
  phase: string;      // idle | preflop | flop | turn | river | showdown | reset
  community: string;  // space-separated revealed board cards
  pot: number;
  currentBet: number; // to-call level this street
  minRaise: number;
  button: number;     // dealer-button seat index
  activeSeat: number; // whose turn, or -1
  timerEndsAt: number;
  seats: PokerSeatView[];
  /** Seat index this client holds, or -1 (owner-only knowledge). */
  mySeat: number;
  /** This client's PRIVATE two hole cards for the current hand (space-separated); "" if none. */
  myHole: string;
}

export const EMPTY_POKER: PokerTableView = {
  phase: "idle",
  community: "",
  pot: 0,
  currentBet: 0,
  minRaise: 0,
  button: -1,
  activeSeat: -1,
  timerEndsAt: 0,
  seats: [],
  mySeat: -1,
  myHole: "",
};

type Listener = (table: PokerTableView) => void;

class PokerStore {
  private table: PokerTableView = EMPTY_POKER;
  private readonly listeners = new Set<Listener>();

  get(): PokerTableView {
    return this.table;
  }

  set(table: PokerTableView): void {
    this.table = table;
    for (const fn of this.listeners) fn(this.table);
  }

  /** Subscribe; immediately invoked with the current value. Returns unsubscribe. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.table);
    return () => this.listeners.delete(fn);
  }
}

export const pokerStore = new PokerStore();
